import { Slot } from '@radix-ui/react-slot';
import React__default, { forwardRef, useMemo } from 'react';
import { MENTION_CHARACTER } from '../../slate/plugins/mentions.mjs';
import { isCommentBodyMention, isCommentBodyLink, toAbsoluteUrl } from './utils.mjs';

const COMMENT_MENTION_NAME = "CommentMention";
const COMMENT_BODY_NAME = "CommentBody";
const COMMENT_LINK_NAME = "CommentLink";
const CommentMention = forwardRef(
  ({ children, asChild, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : "span";
    return /* @__PURE__ */ React__default.createElement(Component, {
      ...props,
      ref: forwardedRef
    }, children);
  }
);
const CommentLink = forwardRef(
  ({ children, asChild, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : "a";
    return /* @__PURE__ */ React__default.createElement(Component, {
      target: "_blank",
      rel: "noopener noreferrer nofollow",
      ...props,
      ref: forwardedRef
    }, children);
  }
);
const defaultBodyComponents = {
  Mention: ({ userId }) => {
    return /* @__PURE__ */ React__default.createElement(CommentMention, null, MENTION_CHARACTER, userId);
  },
  Link: ({ href, children }) => {
    return /* @__PURE__ */ React__default.createElement(CommentLink, {
      href
    }, children);
  }
};
const CommentBody = forwardRef(
  ({ body, components, style, asChild, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : "div";
    const { Mention, Link } = useMemo(
      () => ({ ...defaultBodyComponents, ...components }),
      [components]
    );
    if (!body || !body?.content) {
      return null;
    }
    return /* @__PURE__ */ React__default.createElement(Component, {
      ...props,
      style: { whiteSpace: "break-spaces", ...style },
      ref: forwardedRef
    }, body.content.map((block, index) => {
      switch (block.type) {
        case "paragraph":
          return /* @__PURE__ */ React__default.createElement("p", {
            key: index,
            style: { minHeight: "1lh" }
          }, block.children.map((inline, index2) => {
            if (isCommentBodyMention(inline)) {
              return inline.id ? /* @__PURE__ */ React__default.createElement(Mention, {
                userId: inline.id,
                key: index2
              }) : null;
            }
            if (isCommentBodyLink(inline)) {
              const href = toAbsoluteUrl(inline.url) ?? inline.url;
              return /* @__PURE__ */ React__default.createElement(Link, {
                href,
                key: index2
              }, inline.text ?? inline.url);
            }
            let children = inline.text;
            if (inline.bold) {
              children = /* @__PURE__ */ React__default.createElement("strong", {
                key: index2
              }, children);
            }
            if (inline.italic) {
              children = /* @__PURE__ */ React__default.createElement("em", {
                key: index2
              }, children);
            }
            if (inline.strikethrough) {
              children = /* @__PURE__ */ React__default.createElement("s", {
                key: index2
              }, children);
            }
            if (inline.code) {
              children = /* @__PURE__ */ React__default.createElement("code", {
                key: index2
              }, children);
            }
            return /* @__PURE__ */ React__default.createElement("span", {
              key: index2
            }, children);
          }));
        default:
          return null;
      }
    }));
  }
);
if (process.env.NODE_ENV !== "production") {
  CommentBody.displayName = COMMENT_BODY_NAME;
  CommentMention.displayName = COMMENT_MENTION_NAME;
  CommentLink.displayName = COMMENT_LINK_NAME;
}

export { CommentBody as Body, CommentLink as Link, CommentMention as Mention };
//# sourceMappingURL=index.mjs.map
